import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Button,
  Card,
  Row,
  Col,
  Tag,
  Space,
  message,
  Drawer,
  Divider,
} from 'antd';
import {
  MessageOutlined,
  ArrowRightOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  UserOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import { registerMarketplaceAgent } from '../services/api.service';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

interface MarketplaceAgentDetail {
  input: {
    description: string;
    example: string;
  };
  output: {
    description: string;
    example: string;
  };
  steps: string[];
}

interface MarketplaceAgent {
  key: string;
  name: string;
  description: string;
  tags: string[];
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  detail: MarketplaceAgentDetail;
}

const MARKETPLACE_AGENTS: MarketplaceAgent[] = [
  {
    key: 'meeting_doubts',
    name: 'Analizador de Dudas de Reunión',
    description:
      'Extrae y categoriza las dudas y preocupaciones del cliente a partir de la transcripción de una reunión de ventas. Identifica categorías como precio, plazos, impuestos, sostenibilidad y más.',
    tags: ['Reuniones', 'LLM', 'BigQuery'],
    icon: <MessageOutlined />,
    iconBg: '#e6f4ff',
    iconColor: '#1677ff',
    detail: {
      input: {
        description: 'Texto plano con la transcripción de la reunión. Puede incluir nombres de participantes o ser texto continuo.',
        example: `POST /agent/builder/webhook/{agent_id}
{
  "input": "Cliente: ¿Cuándo podríamos empezar? ¿Hay algún coste adicional de instalación? No sé si encaja con nuestra factura eléctrica actual..."
}`,
      },
      output: {
        description: 'Lista de dudas y preocupaciones del cliente, cada una con categoría, tipo (duda o preocupación) y etiquetas.',
        example: `{
  "node_outputs": {
    "meeting_doubts": [
      {
        "description": "When can we start?",
        "descripcion": "¿Cuándo podríamos empezar?",
        "category": "timing",
        "type": "doubt",
        "tags": ["plazo", "inicio"]
      },
      {
        "description": "Is there an additional installation cost?",
        "descripcion": "¿Hay algún coste adicional de instalación?",
        "category": "price",
        "type": "doubt",
        "tags": ["precio", "instalación"]
      }
    ]
  }
}`,
      },
      steps: [
        'Recibe la transcripción de la reunión como texto plano',
        'El LLM analiza el texto buscando preguntas y preocupaciones del cliente',
        'Clasifica cada duda en categorías: timing, price, taxes, energy, sustainability, logistics, quality, others',
        'Devuelve la lista estructurada con descripción en inglés y español',
      ],
    },
  },
  {
    key: 'estimate_budget',
    name: 'Estimador de Presupuesto',
    description:
      'Analiza la transcripción de una llamada de ventas e identifica automáticamente las respuestas al cuestionario de reforma: metros cuadrados, cocina, baño, suelo, electricidad, climatización y más.',
    tags: ['Presupuesto', 'LLM', 'Reformas'],
    icon: <CalculatorOutlined />,
    iconBg: '#fff7e6',
    iconColor: '#fa8c16',
    detail: {
      input: {
        description: 'Array de transcripciones de llamada con utterances (texto + tipo de participante: internal/external).',
        example: `POST /agent/budget/v1/estimate
{
  "input": [
    {
      "transcription": {
        "content": {
          "utterances": [
            { "text": "reforma completa de 100 metros cuadrados", "participant_type": "internal" },
            { "text": "Sí, vivienda habitual", "participant_type": "external" },
            { "text": "quiero cambiar tuberías y electricidad, cocina y baño entero", "participant_type": "external" }
          ]
        }
      }
    }
  ]
}`,
      },
      output: {
        description: 'Lista de las 15 preguntas del cuestionario con si fueron identificadas, el valor extraído y la cita de la transcripción que lo respalda.',
        example: `{
  "status": "ok",
  "identified_count": 14,
  "total_count": 15,
  "coverage_pct": 93.3,
  "questions": [
    {
      "questionId": "question0",
      "questionCode": "tax",
      "questionLabel": "Uso de vivienda",
      "identified": true,
      "extractedValue": "habitual",
      "confidence": 1.0,
      "evidence": "Cliente: Sí, vivienda habitual"
    },
    {
      "questionId": "question1",
      "questionCode": "metersToReform",
      "questionLabel": "Introduce los m2 totales de la vivienda a reformar:",
      "identified": true,
      "extractedValue": "100",
      "confidence": 1.0,
      "evidence": "Comercial: reforma completa de 100 metros cuadrados"
    }
  ]
}`,
      },
      steps: [
        'Recibe el array de transcripciones con utterances de la llamada',
        'Aplana los utterances en un texto legible distinguiendo Comercial/Cliente',
        'El LLM analiza el texto contra las 15 preguntas fijas del cuestionario de reforma',
        'Extrae el valor exacto en el formato esperado por el sistema (ej: "100", "partial", "1 # 1")',
        'Devuelve cada pregunta con: identified, extractedValue, confidence y evidence (cita textual)',
      ],
    },
  },
  {
    key: 'lead_enrichment',
    name: 'Lead Enrichment',
    description:
      'Extrae datos de contacto (nombre, email, teléfono) de un texto libre y busca el lead correspondiente en el CRM (MongoDB). Maneja distintos formatos de teléfono (+34, con espacios, sin prefijo, etc.).',
    tags: ['CRM', 'LLM', 'MongoDB'],
    icon: <UserOutlined />,
    iconBg: '#f6ffed',
    iconColor: '#52c41a',
    detail: {
      input: {
        description: 'Texto libre con datos del cliente: nombre, teléfono, email, dirección de la reforma, etc.',
        example: `POST /agent/builder/webhook/{agent_id}
{
  "input": "Cliente: Juan García López, teléfono +34 647 475 776, email juan.garcia@gmail.com. Reforma en Av. de los Guindos 42, Málaga."
}`,
      },
      output: {
        description: 'Datos de contacto extraídos y el documento del lead encontrado en MongoDB (si existe).',
        example: `{
  "node_outputs": {
    "lead_enrichment": {
      "extracted": {
        "name": "Juan García López",
        "email": "juan.garcia@gmail.com",
        "phoneNumber": "+34 647 475 776"
      },
      "lead": {
        "_id": "6789abc...",
        "contact": {
          "name": "Juan García López",
          "phone": "647475776",
          "email": "juan.garcia@gmail.com"
        },
        "status": "active"
      },
      "found": true
    }
  }
}`,
      },
      steps: [
        'Recibe texto libre con datos del cliente',
        'El LLM extrae nombre, email y teléfono como structured output',
        'Busca en MongoDB (colección Lead) por teléfono, email o nombre',
        'El teléfono se normaliza para coincidir con distintos formatos (+34, espacios, guiones)',
        'Devuelve los datos extraídos y el lead encontrado (o null si no existe)',
      ],
    },
  },
];

const MarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [detailAgent, setDetailAgent] = useState<MarketplaceAgent | null>(null);

  const handleUse = async (agentKey: string) => {
    setLoadingKey(agentKey);
    try {
      const result = await registerMarketplaceAgent(agentKey);
      navigate(`/builder/${result.agent_id}`);
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al registrar el agente');
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>

      <Content style={{ padding: '32px', maxWidth: 1280, margin: '0 auto', width: '100%' }}>
        {/* Section title */}
        <div style={{ marginBottom: 32 }}>
          <Title level={3} style={{ margin: '0 0 8px 0' }}>
            Agentes Disponibles
          </Title>
          <Text type="secondary">
            Agentes preconfigurados y optimizados listos para usar. Selecciona uno y ejecútalo directamente con tus datos.
          </Text>
        </div>

        <Row gutter={[24, 24]}>
          {MARKETPLACE_AGENTS.map((agent) => (
            <Col xs={24} sm={12} lg={8} key={agent.key}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                styles={{
                  body: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: 24,
                  },
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: agent.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    color: agent.iconColor,
                    marginBottom: 16,
                  }}
                >
                  {agent.icon}
                </div>

                {/* Name */}
                <Title level={5} style={{ margin: '0 0 8px 0' }}>
                  {agent.name}
                </Title>

                {/* Description */}
                <Paragraph
                  type="secondary"
                  style={{ fontSize: 13, flex: 1, marginBottom: 16 }}
                >
                  {agent.description}
                </Paragraph>

                {/* Tags */}
                <Space size={4} wrap style={{ marginBottom: 20 }}>
                  {agent.tags.map((tag) => (
                    <Tag
                      key={tag}
                      icon={tag === 'BigQuery' ? <DatabaseOutlined /> : undefined}
                      color={
                        tag === 'LLM'
                          ? 'blue'
                          : tag === 'BigQuery'
                          ? 'green'
                          : 'default'
                      }
                    >
                      {tag}
                    </Tag>
                  ))}
                </Space>

                {/* Actions */}
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <Button
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    iconPosition="end"
                    block
                    loading={loadingKey === agent.key}
                    onClick={() => handleUse(agent.key)}
                  >
                    Usar
                  </Button>
                  <Button
                    block
                    icon={<InfoCircleOutlined />}
                    onClick={() => setDetailAgent(agent)}
                  >
                    Ver detalles
                  </Button>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </Content>

      {/* Detail Drawer */}
      <Drawer
        title={
          <Space>
            {detailAgent && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: detailAgent.iconBg,
                  color: detailAgent.iconColor,
                  fontSize: 14,
                }}
              >
                {detailAgent.icon}
              </span>
            )}
            {detailAgent?.name}
          </Space>
        }
        placement="right"
        width={600}
        open={!!detailAgent}
        onClose={() => setDetailAgent(null)}
        styles={{ body: { paddingBottom: 80, overflowY: 'auto' } }}
        footer={
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            iconPosition="end"
            block
            size="large"
            loading={detailAgent ? loadingKey === detailAgent.key : false}
            onClick={() => detailAgent && handleUse(detailAgent.key)}
          >
            Usar este agente
          </Button>
        }
      >
        {detailAgent && (
          <>
            <Paragraph type="secondary">{detailAgent.description}</Paragraph>

            <Space size={4} wrap style={{ marginBottom: 24 }}>
              {detailAgent.tags.map((tag) => (
                <Tag
                  key={tag}
                  icon={tag === 'BigQuery' ? <DatabaseOutlined /> : undefined}
                  color={tag === 'LLM' ? 'blue' : tag === 'BigQuery' ? 'green' : 'default'}
                >
                  {tag}
                </Tag>
              ))}
            </Space>

            <Divider orientation="left" orientationMargin={0}>
              <Text strong style={{ fontSize: 13 }}>Cómo funciona</Text>
            </Divider>
            <ol style={{ paddingLeft: 20, marginBottom: 24 }}>
              {detailAgent.detail.steps.map((step, i) => (
                <li key={i} style={{ marginBottom: 8, fontSize: 13, color: '#595959' }}>
                  {step}
                </li>
              ))}
            </ol>

            <Divider orientation="left" orientationMargin={0}>
              <Space size={4}>
                <ArrowDownOutlined style={{ color: '#52c41a' }} />
                <Text strong style={{ fontSize: 13 }}>Entrada esperada</Text>
              </Space>
            </Divider>
            <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
              {detailAgent.detail.input.description}
            </Paragraph>
            <pre
              style={{
                background: '#1e1e2e',
                color: '#cdd6f4',
                padding: '12px 16px',
                borderRadius: 8,
                fontSize: 12,
                overflowX: 'auto',
                marginBottom: 24,
                lineHeight: 1.6,
              }}
            >
              {detailAgent.detail.input.example}
            </pre>

            <Divider orientation="left" orientationMargin={0}>
              <Space size={4}>
                <ArrowUpOutlined style={{ color: '#1677ff' }} />
                <Text strong style={{ fontSize: 13 }}>Salida del agente</Text>
              </Space>
            </Divider>
            <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
              {detailAgent.detail.output.description}
            </Paragraph>
            <pre
              style={{
                background: '#1e1e2e',
                color: '#cdd6f4',
                padding: '12px 16px',
                borderRadius: 8,
                fontSize: 12,
                overflowX: 'auto',
                lineHeight: 1.6,
              }}
            >
              {detailAgent.detail.output.example}
            </pre>
          </>
        )}
      </Drawer>
    </Layout>
  );
};

export default MarketplacePage;
